import { loganLog } from '@RootCommon/utils/logan'
import { owlReportMetricsWeb } from '@RootCommon/utils/owl'
import { deviceHeartBeat } from '@/common/api/omReport'
import { addHeartBeatData } from './deviceHeartBeatData'
import { requestIdleCallbackPolyfill } from './polyfill'
import timeoutWorker from '../timeoutWorker'
import { getNetworkInfo } from './network'
import { NETWORK_ERROR_CUSTOM_EVENT } from '@/common/context/NetworkErrorContext'
import { getLoginShopid } from '@RootCommon/utils'

const DEFAULT_REPORT_GAP_TIME = 30000 // 兜底30s上报一次

const NETWORK_TYPE_MAP = {
  '2g': 2,
  '3g': 3,
  '4g': 4,
  'slow-2g': -2,
}

const NO_HEART_BEAT_TIME = 3 * 60 * 1000 // 3min无心跳
let heartBeatFailedstartTime = null // "3min无心跳"的起始计算时间
let lastHeartBeatDuration: number | null = null

/**
 * 心跳上报，如果3min内无心跳，则说明无网或弱网，需要通知electron展示no network页面
 */
export function reportDeviceHeartBeat (
  deviceType: number,
  heartReportType: 1 | 2 = 1,
  needReportHeartBeatData = false, // 是否需要上报心跳数据
  isFirstCall = true,
  isOnlyReportOnce = false,
) {
  const shopId = getLoginShopid()
  /**
   * 同步检查：在函数入口立即检查，避免创建不必要的executeHeartBeat和调度，节省资源
   */
  if (shopId <= 0) {
    return
  }

  const executeHeartBeat = async () => {
    /**
     * 异步检查：先检查闭包中的shopId，如果已经无效则直接return
     * 如果闭包中的shopId有效，再获取最新的shopId检查，防止在异步执行过程中shopId变成-1
     */
    if (shopId <= 0) {
      return
    }
    const currentShopId = getLoginShopid()
    if (currentShopId <= 0) {
      loganLog({ msg: '心跳上报取消：shopId无效', currentShopId })
      return
    }

    // raptor上报 - 心跳上报触发数
    owlReportMetricsWeb({ 'device_heart_beat_trigger': 1 })
    loganLog({ msg: '心跳上报触发', shopId: currentShopId })

    const startTime = Date.now()
    const connectionData = (navigator as any)?.connection
    const networkInfo = await getNetworkInfo()
    const urlTimeArr = networkInfo.urlTime
    const validDelayTimes = urlTimeArr.filter(item => item.time !== -1)
    const averageDelayTime = validDelayTimes.length > 0 ? validDelayTimes.reduce((a, b) => a + b.time, 0) / validDelayTimes.length : 99999
    let reportGapTime = DEFAULT_REPORT_GAP_TIME
    let isFailed = false
    try {
      const res = await deviceHeartBeat(deviceType, heartReportType, {
        networkType: NETWORK_TYPE_MAP[connectionData?.effectiveType] || -2,
        networkDelay: lastHeartBeatDuration !== null
          ? Number(lastHeartBeatDuration.toFixed(2))
          : Number(averageDelayTime.toFixed(2)),
      })
      reportGapTime = res.reportGapTime ? res.reportGapTime * 1000 : DEFAULT_REPORT_GAP_TIME
      heartBeatFailedstartTime = null
      // 心跳成功，通知在线，隐藏 NetworkError
      window.dispatchEvent(new CustomEvent(NETWORK_ERROR_CUSTOM_EVENT.ONLINE))
      if (!res.reportGapTime) {isFailed = true}
      // raptor上报 - 心跳上报成功数
      owlReportMetricsWeb({ 'device_heart_beat_success': 1 })
      loganLog({ msg: '心跳上报成功' })
    } catch (e) {
      isFailed = true
      if (heartBeatFailedstartTime === null) {
        heartBeatFailedstartTime = Date.now()
      }
      if (Date.now() - heartBeatFailedstartTime >= NO_HEART_BEAT_TIME) {
        // 3min无心跳，展示 NetworkError
        window.dispatchEvent(new CustomEvent(NETWORK_ERROR_CUSTOM_EVENT.OFFLINE))
        loganLog({ msg: '3min无心跳，展示 NetworkError' })
        // 调用electron桥，通知3min无心跳事件【不需要了，放在bpc做】
        // window.electronAPI?.heartbeatError?.()
        // 重新计时，防止重复通知electron
        heartBeatFailedstartTime = Date.now()
      }
      // raptor上报 - 心跳上报失败数
      owlReportMetricsWeb({ 'device_heart_beat_error': 1 })
      loganLog({ msg: '心跳上报接口调用失败', error: JSON.stringify(e) })
    }
    const endTime = Date.now()
    const responseTime = endTime - startTime
    lastHeartBeatDuration = responseTime
    // 如果reportGapTime为空，则认为心跳失败
    needReportHeartBeatData && addHeartBeatData(startTime, responseTime, isFailed)

    if (!isOnlyReportOnce) {
      timeoutWorker.setTimeout(
        reportGapTime,
        () => {
          reportDeviceHeartBeat(deviceType, heartReportType, needReportHeartBeatData, false, false)
        },
      )
    }
  }

  if (isFirstCall) {
    (window.requestIdleCallback || requestIdleCallbackPolyfill)(executeHeartBeat)
  } else {
    executeHeartBeat()
  }
}